import {stateToHTML} from 'draft-js-export-html'
import Mustache from 'mustache'

import {
  ContentState,
  ContentBlock,
  EditorState,
  DraftHandleValue,
  SelectionState,
  Modifier,
  RawDraftContentState,
  convertFromRaw,
  convertToRaw,
} from 'draft-js'

import {
  mdToDraftjs,
  draftjsToMd,
} from 'draftjs-md-converter'

import {
  getAllBlocks,
} from 'draftjs-utils'

import {
  ITextEditor_EditorUpdate,
  ITextEditor_Entity_MagicTag,
  ITextEditor_Entity_Link,
} from '../types/texteditor'

import {
  IData_MagicTag_Instance,
  IData_MagicTag_Render_Values,
} from '../types/schema/magic_tag'

import {
  TEXT_EDITOR_LINK_TYPE_TEXT,
  TEXT_EDITOR_LINK_TYPE_ASTONISH_OPEN_BROWSER,
  TEXT_EDITOR_LINK_TYPE_ASTONISH_UNSUBSCRIBE,
  TEXT_EDITOR_LINK_TYPE_FILE,
  TEXT_EDITOR_LINK_TYPE_KNOWLEDGEBASE,
  TEXT_EDITOR_LIST_TYPES,
  TEXT_EDITOR_DEFAULT_FONT,
  TEXT_EDITOR_HORIZONTAL_ALIGN_DEFAULT,
  TEXT_EDITOR_ENTITY_TYPE_LINK,
  TEXT_EDITOR_ENTITY_TYPE_MAGICTAG,
} from '../constants'

import {
  StorageFile,
} from '../utils/storage'

export interface ITextSelection {
  startKey: string,
  startOffset: number,
  endKey: string,
  endOffset: number,
}

export const getSelection = (editorState: EditorState): ITextSelection => {
  const selectionState = editorState.getSelection()
  const startKey = selectionState.getStartKey()
  const endKey = selectionState.getEndKey()
  const startOffset = selectionState.getStartOffset()
  const endOffset = selectionState.getEndOffset()
  return {
    startKey,
    startOffset,
    endKey,
    endOffset,
  }
}

export const applySelection = (editorState: EditorState, selection: ITextSelection): EditorState => {
  return EditorState.forceSelection(editorState, new SelectionState({
    anchorKey: selection.startKey,
    anchorOffset: selection.startOffset,
    focusKey: selection.endKey,
    focusOffset: selection.endOffset,
  }))
}

export const getSelectedText = (editorState: EditorState): string[] => {
  const selectionState = editorState.getSelection();
  const contentState = editorState.getCurrentContent();

  let selectedTextArray: string[] = [];

  const startKey = selectionState.getStartKey();
  const endKey = selectionState.getEndKey();
  const startOffset = selectionState.getStartOffset();
  const endOffset = selectionState.getEndOffset();

  const contentBlockArray = contentState.getBlocksAsArray();

  let isSelecting = false;

  for (let block of contentBlockArray) {
    const key = block.getKey();
    let blockText = '';

    if (key === startKey) {
      isSelecting = true;
      blockText = block.getText().slice(startOffset);
    } else if (isSelecting) {
      blockText = block.getText();
    }

    if (key === endKey) {
      isSelecting = false;
      blockText = block.getText().slice(0, endOffset);
    }

    if (isSelecting || key === endKey) {
      selectedTextArray.push(blockText);
    }

    if (key === endKey) {
      break;
    }
  }

  return selectedTextArray;
}

export const handleReturnFactory = (updateState: ITextEditor_EditorUpdate) => (e: any, editorState: EditorState): DraftHandleValue => {
  const selection = editorState.getSelection()
  if (selection.isCollapsed()) {
    const contentState = editorState.getCurrentContent()
    const startKey = selection.getStartKey()
    const currentBlock = contentState.getBlockForKey(startKey)
    if (currentBlock) {
      const blockData = currentBlock.getData()
      if (blockData.size > 0) {
        const newContentState = Modifier.splitBlock(
          editorState.getCurrentContent(),
          editorState.getSelection()
        )
        let splitState = EditorState.push(editorState, newContentState, 'split-block')
        const target = splitState.getSelection()
        const afterMergeStylesContentState = Modifier.mergeBlockData(
          splitState.getCurrentContent(),
          target,
          blockData,
        )
        splitState = EditorState.push(editorState, afterMergeStylesContentState, 'split-block')
        updateState(splitState)
        return "handled"
      }
    }
  }
  return "not-handled"
}

export const isBlockList = (block: ContentBlock): boolean => {
  const type = block.getType()
  return TEXT_EDITOR_LIST_TYPES.indexOf(type) >= 0 
}

export const getBlockAlignment = (block: ContentBlock): string => {
  return isBlockList(block) ?
    TEXT_EDITOR_HORIZONTAL_ALIGN_DEFAULT :
    block.getData().get('align') || 'inherit'
}

export const getBlockFont = (block: ContentBlock): string => {
  return block.getData().get('font') || TEXT_EDITOR_DEFAULT_FONT
}

export const getAlignClassname = (align: string) => `TextRender-align-${align}`
export const getFontClassname = (font: string) => `TextRender-font-${font}`


export const getColors = (contentState: ContentState): string[] => {
  const editorState = EditorState.createWithContent(contentState)
  const blocks: ContentBlock[] = getAllBlocks(editorState)
  const allColors: Record<string, boolean> = {}
  blocks.forEach(block => {
    const data = block.getData()
    const color = data.get('color-font')
    const fillcolor = data.get('color-fill')
    if(color) allColors[color.color] = true
    if(fillcolor) allColors[fillcolor.color] = true
  })
  return Object.keys(allColors)
}

export const getColorName = (color: string) => color.replace(/^\#/, '')
export const getTagClassName = (tag: string) => `TextRender-block TextRender-tag-${tag}`
export const getColorClassName = (color: string) => `TextRender-color-${getColorName(color)}`
export const getFillColorClassName = (color: string) => `TextRender-fillcolor-${getColorName(color)}`

export const getColorStyles = (contentState: ContentState): string => {
  const colors = getColors(contentState)
  return colors.map(color => {
    return `
.${getColorClassName(color)} {
  color: ${color};
}
.${getFillColorClassName(color)} {
  background-color: ${color};
}
`
  }).join("\n")
}

// add classnames to blocks
// used by the draft editor
export const blockClassnameFn = (block: ContentBlock, mode = 'editor') => {
  const color = block.getData().get('color-font')
  const fillcolor = block.getData().get('color-fill')
  return [
    getTagClassName(block.getType()),
    getAlignClassname(getBlockAlignment(block)),
    getFontClassname(getBlockFont(block)),
    color ? getColorClassName(color.color) : null,
    fillcolor ? getFillColorClassName(fillcolor.color) : null,
  ].filter(c => c).join(' ')
}

// add style props to blocks
// this is used by the HTML exporter
export const blockStyleFn = (block: ContentBlock) => {
  const className = blockClassnameFn(block, 'render')
  const attributes = {
    class: className,
  }
  return {
    attributes,
  }
}

export const flattenMagicTag = (
  tag: ITextEditor_Entity_MagicTag,
) => {
  return `{{{ ${tag.type}.${tag.field} }}}`
}

// render a mustache template with the given values
export const renderMagicString = (
  template: string,
  values: IData_MagicTag_Render_Values,
): string => {
  return Mustache.render(template, values)
}

export const flattenLink = (
  link: ITextEditor_Entity_Link,
): string => {
  if(link.type == TEXT_EDITOR_LINK_TYPE_TEXT) {
    if(!link?.url) return '#'
    const contentState = getContentFromRaw(link.url)
    return stateToHTML(contentState as any, {
      blockRenderers: {
        unstyled: (block) => {
          const text = block.getText()
          let returnValue = ''
          let currentEntityKey = ''
          text.split('').forEach((char, i) => {
            const entityKey = block.getEntityAt(i)
            if(!entityKey) {
              currentEntityKey = ''
              returnValue += char
              return
            }
            if(!currentEntityKey) {
              currentEntityKey = entityKey
              const entity = contentState.getEntity(entityKey)
              if(entity.getType() == TEXT_EDITOR_ENTITY_TYPE_MAGICTAG) {
                const tag = entity.getData() as ITextEditor_Entity_MagicTag
                returnValue += flattenMagicTag(tag)
              }
            }
          })
          return returnValue
        },
      },
    })
  }
  else if(link.type == TEXT_EDITOR_LINK_TYPE_ASTONISH_OPEN_BROWSER) {
    return `{{{ astonish_open_browser.url }}}`
  }
  else if(link.type == TEXT_EDITOR_LINK_TYPE_ASTONISH_UNSUBSCRIBE) {
    return `{{{ astonish_unsubscribe.url }}}`
  }
  else if(link.type == TEXT_EDITOR_LINK_TYPE_FILE && link.file) {
    const file = StorageFile(link.file)
    return file.url()
  }
  else if(link.type == TEXT_EDITOR_LINK_TYPE_KNOWLEDGEBASE && link.knowledgebase) {
    const config = link.knowledgebase
    return `/knowledgebase/${config.type}/view/${config.id}/page/${config.page}`
  }
  else {
    return '#other'
  }
}

export const exportHTML = (contentState: ContentState): string => {
  const html = stateToHTML(contentState as any, {
    defaultBlockTag: 'div',
    blockStyleFn,
    entityStyleFn: (entity) => {
      const entityType = entity.getType()
      if (entityType === TEXT_EDITOR_ENTITY_TYPE_MAGICTAG) {
        const tag = entity.getData() as ITextEditor_Entity_MagicTag
        return {
          element: 'span',
          attributes: {
            'data-magictag-type': tag.type,
            'data-magictag-field': tag.field,
            'data-magictag-default': tag.default,
          }
        }
      }
      else if(entityType === TEXT_EDITOR_ENTITY_TYPE_LINK) {
        const link = entity.getData() as ITextEditor_Entity_Link
        const url = flattenLink(link)
        return {
          element: 'a',
          attributes: {
            'href': url,
            'target': `_${link.target || 'blank'}`,
          }
        }
      }
    },
  })
  return html.replace(/<span data-magictag-type="(\w+?)" data-magictag-field="(\w+?)" data-magictag-default="(.*?)">.*?<\/span>/gsm, (match, type, field, defaultValue) => {
    const tag: ITextEditor_Entity_MagicTag = {
      type,
      field,
      default: defaultValue,
    }
    return flattenMagicTag(tag)
  });
}

export const moveSelectionToEnd = (editorState: EditorState): EditorState => {
  const content = editorState.getCurrentContent()
  const blockMap = content.getBlockMap()

  const key = blockMap.last().getKey()
  const length = blockMap.last().getLength()

  // On Chrome and Safari, calling focus on contenteditable focuses the
  // cursor at the first character. This is something you don't expect when
  // you're clicking on an input element but not directly on a character.
  // Put the cursor back where it was before the blur.
  const selection = new SelectionState({
    anchorKey: key,
    anchorOffset: length,
    focusKey: key,
    focusOffset: length,
  })
  return EditorState.forceSelection(editorState, selection)
}

export const injectMagicTag = (
  contentState: ContentState,
  selection: SelectionState,
  tag: IData_MagicTag_Instance,
  addExtraCharacters = '',
): ContentState => {
  const contentStateWithEntity = contentState.createEntity(TEXT_EDITOR_ENTITY_TYPE_MAGICTAG, 'IMMUTABLE', tag)
  const entityKey = contentStateWithEntity.getLastCreatedEntityKey()
  const entityText = (tag.text || tag.field) + (tag.default ? ` | ${tag.default}` : '')
  const textWithEntity = Modifier.insertText(contentState, selection, entityText, undefined, entityKey)
  if(!addExtraCharacters) return textWithEntity
  const newSelection = new SelectionState({
    anchorKey: selection.getAnchorKey(),
    anchorOffset: selection.getAnchorOffset() + entityText.length,
    focusKey: selection.getAnchorKey(),
    focusOffset: selection.getAnchorOffset() + entityText.length,
  })
  return Modifier.insertText(textWithEntity, newSelection, addExtraCharacters)
}

export const getMagicTags = (
  value: RawDraftContentState,
): IData_MagicTag_Instance[] => {
  if(!value) return []
  const allEntities = Object.values(value.entityMap || {})
  const tagsInLinks = allEntities
    .filter(entity => entity.type == TEXT_EDITOR_ENTITY_TYPE_LINK)
    .reduce<IData_MagicTag_Instance[]>((all, entity) => {
      const data = entity.data as ITextEditor_Entity_Link
      return all.concat(getMagicTags(data.url))
    }, [])
  const tagsInText = allEntities
    .filter(entity => entity.type == TEXT_EDITOR_ENTITY_TYPE_MAGICTAG)
    .map(entity => {
      const data = entity.data as ITextEditor_Entity_MagicTag
      return data
    })
  return tagsInLinks.concat(tagsInText)
}

export const getRawStateFromString = (
  text: string,
): RawDraftContentState => {
  return convertToRaw(ContentState.createFromText(text))
}

export const getContentFromRaw = (
  raw: RawDraftContentState,
): ContentState => convertFromRaw(raw)

export const getBlockId = (length = 5): string => {
  var result           = ''
  var characters       = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  var charactersLength = characters.length
  for ( var i = 0; i < length; i++ ) {
     result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result
}

export const getRawFromMarkdown = (
  markdown: string,
): RawDraftContentState => {
  const rawDraft = mdToDraftjs(markdown)
  // we now need to map all links into draft format
  const allEntities = Object.values(rawDraft.entityMap)
  allEntities
    .filter(entity => entity.type == TEXT_EDITOR_ENTITY_TYPE_LINK)
    .forEach((entity) => {
      const url = entity.data.url
      entity.data.url = {
        blocks: [
          {
            key: getBlockId(),
            text: url,
            type: 'unstyled',
            depth: 0,
            inlineStyleRanges: [],
            entityRanges: [],
            data: {}
          }
        ],
        entityMap: {}
      }
    })

  return rawDraft
}

export const getMarkdownFromRaw = (
  raw: RawDraftContentState,
): string => draftjsToMd(raw)